/**
 * MediaLightbox — overlay de mídia em tela cheia.
 * Portal no body evita corte por overflow/transform dos ancestrais do chat.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AppIcon } from '../../../../../components/icons/AppIcon';
import { isTemporaryRemoteMediaUrl } from '../../../../../utils/failedImageCache';

export interface LightboxItem {
    url: string;
    type: 'image' | 'video';
    caption?: string;
}

interface MediaLightboxProps {
    items: LightboxItem[];
    initialIndex: number;
    onClose: () => void;
}

export const MediaLightbox: React.FC<MediaLightboxProps> = ({ items, initialIndex, onClose }) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const current = items[currentIndex];
    const hasMultiple = items.length > 1;

    const goNext = useCallback(() => {
        setCurrentIndex((prev) => (prev + 1) % items.length);
    }, [items.length]);

    const goPrev = useCallback(() => {
        setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
    }, [items.length]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowRight') goNext();
            if (e.key === 'ArrowLeft') goPrev();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose, goNext, goPrev]);

    if (!current || typeof document === 'undefined') return null;

    return createPortal(
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Visualizar mídia"
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90"
            onClick={onClose}
        >
            <img
                src="/logo%20principal%20dark%20140x40.png"
                alt="Adlitics"
                width={140}
                height={40}
                className="pointer-events-none absolute top-[max(1rem,env(safe-area-inset-top))] left-[max(1.25rem,env(safe-area-inset-left))] z-10 h-7 w-auto object-contain md:h-8"
                draggable={false}
            />

            <button
                type="button"
                onClick={onClose}
                aria-label="Fechar"
                className="absolute top-[max(1rem,env(safe-area-inset-top))] right-[max(1.25rem,env(safe-area-inset-right))] z-10 inline-flex size-11 items-center justify-center rounded-full bg-card/10 hover:bg-card/20"
            >
                <AppIcon name="x" className="size-6 text-white" />
            </button>

            {hasMultiple && (
                <p className="absolute top-[max(1rem,env(safe-area-inset-top))] left-1/2 z-10 -translate-x-1/2 rounded-full bg-black/40 px-s3 py-s1 text-sm text-white/80">
                    {currentIndex + 1} / {items.length}
                </p>
            )}

            {hasMultiple && (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); goPrev(); }}
                    aria-label="Anterior"
                    className="absolute left-[max(1rem,env(safe-area-inset-left))] top-1/2 z-10 hidden size-11 -translate-y-1/2 items-center justify-center rounded-full bg-card/10 hover:bg-card/20 sm:inline-flex"
                >
                    <AppIcon name="chevron-left" className="size-8 text-white" />
                </button>
            )}

            <figure
                className="flex max-h-[85vh] max-w-[90vw] flex-col items-center"
                onClick={(e) => e.stopPropagation()}
            >
                {current.type === 'image' && !isTemporaryRemoteMediaUrl(current.url) ? (
                    <img
                        src={current.url}
                        alt={current.caption || 'Mídia'}
                        className="max-h-[80vh] max-w-full rounded-lg object-contain shadow-2xl"
                        draggable={false}
                    />
                ) : current.type === 'image' ? (
                    <div className="flex size-48 items-center justify-center rounded-lg bg-white/10 text-white/70">
                        <AppIcon name="image" className="size-12" aria-hidden />
                    </div>
                ) : !isTemporaryRemoteMediaUrl(current.url) ? (
                    <video src={current.url} controls autoPlay className="max-h-[80vh] max-w-full rounded-lg shadow-2xl" />
                ) : (
                    <div className="flex size-48 items-center justify-center rounded-lg bg-white/10 text-white/70">
                        <AppIcon name="video" className="size-12" aria-hidden />
                    </div>
                )}
                {current.caption ? (
                    <figcaption className="mt-s3 max-w-lg text-center text-sm text-white/80">
                        {current.caption}
                    </figcaption>
                ) : null}
            </figure>

            {hasMultiple && (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); goNext(); }}
                    aria-label="Próxima"
                    className="absolute right-[max(1rem,env(safe-area-inset-right))] top-1/2 z-10 hidden size-11 -translate-y-1/2 items-center justify-center rounded-full bg-card/10 hover:bg-card/20 sm:inline-flex"
                >
                    <AppIcon name="chevron-right" className="size-8 text-white" />
                </button>
            )}

            {hasMultiple && (
                <nav
                    aria-label="Miniaturas"
                    className="absolute bottom-[max(1rem,env(safe-area-inset-bottom))] left-1/2 flex max-w-[80vw] -translate-x-1/2 gap-s2 overflow-x-auto px-s4 py-s2"
                >
                    {items.map((item, i) => (
                        <button
                            key={`${item.url}-${i}`}
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setCurrentIndex(i); }}
                            aria-label={`Mídia ${i + 1}`}
                            aria-current={i === currentIndex ? 'true' : undefined}
                            className={`size-14 shrink-0 overflow-hidden rounded-lg border-2 transition-all ${
                                i === currentIndex ? 'scale-110 border-white' : 'border-transparent opacity-60 hover:opacity-80'
                            }`}
                        >
                            {item.type === 'image' && !isTemporaryRemoteMediaUrl(item.url) ? (
                                <img src={item.url} alt="" className="size-full object-cover" />
                            ) : (
                                <span className="flex size-full items-center justify-center bg-gray-700">
                                    <AppIcon name={item.type === 'image' ? 'image' : 'video'} className="size-5 text-white" aria-hidden />
                                </span>
                            )}
                        </button>
                    ))}
                </nav>
            )}
        </div>,
        document.body,
    );
};
